import * as React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useLingui } from "@lingui/react/macro";
import { Button } from "./button";
import { XIcon } from "lucide-react";

const badgeVariants = cva(
  "inline-flex items-center justify-center border border-transparent font-medium w-fit whitespace-nowrap shrink-0 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground",
        yellow: "border-yellow-50 bg-yellow-50 text-yellow-800",
        purple: "border-purple-50 bg-purple-50 text-purple-900",
        red: "border-red-50 bg-red-50 text-red-700",
        green: "border-green-50 bg-green-50 text-green-700",
        lime: "border-lime-50 bg-lime-50 text-lime-900",
        pink: "border-pink-50 bg-pink-50 text-pink-900",
        blue: "border-blue-50 bg-blue-50 text-blue-700",
        violet: "border-violet-50 bg-violet-50 text-violet-700",
        cyan: "border-cyan-200 bg-cyan-50 text-cyan-700",
        fuchsia: "border-fuchsia-50 bg-fuchsia-50 text-fuchsia-700",
        secondary: "bg-secondary text-secondary-foreground",
        destructive:
          "bg-destructive text-white focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline: "border-border text-foreground",
        link: "text-primary underline-offset-4",
      },
      size: {
        default: "px-2 py-0.5 text-xs [&>svg]:size-3",
        lg: "text-sm h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 [&>svg]:size-4 ",
      },
      shape: {
        round: "rounded-full",
        square: "rounded-md",
      },
      intensity: {
        default: "",
        strong: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "round",
    },
    compoundVariants: [
      {
        shape: "square",
        size: "lg",
        className: "rounded-md",
      },
      {
        shape: "square",
        size: "default",
        className: "rounded-sm",
      },
      {
        intensity: "strong",
        variant: "yellow",
        className:
          "border-yellow-100 bg-yellow-100 [a&]:hover:bg-yellow-500/90",
      },
      {
        intensity: "strong",
        variant: "purple",
        className:
          "border-purple-200 bg-purple-200 [a&]:hover:bg-purple-500/90",
      },
      {
        intensity: "strong",
        variant: "red",
        className: "border-red-100 bg-red-100 [a&]:hover:bg-red-500/90",
      },
      {
        intensity: "strong",
        variant: "pink",
        className: "border-pink-200 bg-pink-200 [a&]:hover:bg-pink-500/90",
      },
      {
        intensity: "strong",
        variant: "green",
        className: "border-green-100 bg-green-100 [a&]:hover:bg-green-500/90",
      },
      {
        intensity: "strong",
        variant: "lime",
        className: "border-lime-200 bg-lime-200 [a&]:hover:bg-lime-500/90",
      },
      {
        intensity: "strong",
        variant: "blue",
        className: "border-blue-100 bg-blue-100 [a&]:hover:bg-blue-500/90",
      },
      {
        intensity: "strong",
        variant: "violet",
        className:
          "border-violet-100 bg-violet-100 [a&]:hover:bg-violet-500/90",
      },
      {
        intensity: "strong",
        variant: "cyan",
        className: "border-cyan-100 bg-cyan-100 [a&]:hover:bg-cyan-500/90",
      },
      {
        intensity: "strong",
        variant: "fuchsia",
        className:
          "border-fuchsia-100 bg-fuchsia-100 [a&]:hover:bg-fuchsia-500/90",
      },
    ],
  }
);

export type BadgeProps = useRender.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    onDelete?: () => void;
  };

function BadgeComponent(
  {
    className,
    intensity = "default",
    variant = "default",
    size = "default",
    shape = "round",
    render,
    children,
    onDelete,
    onClick,
    onDoubleClick,
    onMouseDown,
    ...props
  }: BadgeProps,
  ref: React.Ref<HTMLSpanElement>
) {
  const { t } = useLingui();

  return useRender({
    defaultTagName: "span",
    ref,
    props: mergeProps<"span">(
      {
        className: cn(
          badgeVariants({ variant, size, shape, intensity }),
          className
        ),
        children: (
          <>
            {onClick ? (
              <button
                type="button"
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                onMouseDown={onMouseDown}
                className="badge-inner"
                tabIndex={-1}
              >
                {children}
              </button>
            ) : (
              children
            )}
            {onDelete && (
              <Button
                aria-label={t`Ta bort`}
                title={t`Ta bort`}
                size="icon-xs"
                variant="ghost"
                onClick={onDelete}
                className="badge-delete transition-none w-min"
                tabIndex={-1}
                type="button"
              >
                <XIcon />
              </Button>
            )}
          </>
        ),
      },
      onClick ? props : { onDoubleClick, onMouseDown, ...props }
    ),
    render,
    state: {
      slot: "badge",
      variant,
      size,
      shape,
    },
  });
}

const Badge = React.forwardRef(BadgeComponent);

export { Badge, badgeVariants };
